/**
 * 답변에 딸린 행동(예약 버튼·전화·문의 링크)의 **한 규격**.
 *
 * ## 왜 한 곳인가
 *
 * 파이프라인은 답변 글과 함께 meta 로 "예약 링크·버튼 라벨·버튼을 꼭 붙일 턴인가(ctaForce)·
 * 예약 요약·사장님 연결(전화/문의폼)"을 내려보낸다. 그걸 **버튼으로 바꾸는 판정**이 채널마다
 * 따로 있었고, 이미 갈라져 있었다(2026-09-24 대조):
 *
 *   웹 위젯   주소(호스트+경로)가 같으면 같은 링크 — 모델이 utm 을 흘려도 버튼이 붙는다
 *   카카오    글자가 똑같아야 같은 링크 — 한 글자만 달라도 버튼 대신 맨 URL 이 나갔다
 *   네이버    카카오와 같음
 *   인스타    판정 자체가 없었다 — 예약 가능 여부를 물은 턴에 모델이 링크를 안 쓰면
 *             손님에게 갈 곳이 없었다
 *
 * 그래서 판정과 본문 정리를 여기 하나로 모으고, 채널은 **그리는 것만** 한다.
 * 규칙을 고칠 때 한 곳만 고치면 네 채널이 같이 바뀐다.
 *
 * 이 파일은 **아무것도 import 하지 않는다** — 웹 위젯 번들(esbuild)도 같은 파일을 쓴다.
 */

export type ReplyAction =
  /** 예약하기. url 은 언제나 서버가 준 값이다(모델이 쓴 URL 이 아니다). */
  | { kind: 'reservation'; url: string; label?: string; summary?: string }
  /** 사장님 전화. phone 은 화면에 보일 원문, tel 은 걸 번호(숫자·+ 만). */
  | { kind: 'call'; phone: string; tel: string }
  /** 사장님 문의폼. http(s) 만 통과한다. */
  | { kind: 'contact'; url: string }
  /**
   * 다음 질문 — 누르면 이 문장을 손님 말로 보낸다(사이드톡의 user_utterance 와 같은 자리).
   * 사장님 추천 질문 칩 제목이라 프리셋이 있으면 위젯이 LLM 없이 답한다(follow-ups.ts).
   */
  | { kind: 'ask'; text: string };

/** 파이프라인 meta 에서 행동을 만드는 데 쓰는 값들. */
export interface ReplyMeta {
  reservationUrl?: string;
  ctaLabel?: string;
  /** 본문에 링크가 없어도 예약 버튼을 붙일 턴(손님이 예약 가능 여부를 대놓고 물었다). */
  ctaForce?: boolean;
  bookingSummary?: string;
  handoff?: { phone?: string; contactUrl?: string };
  /**
   * 버튼(예약 행동)이 실제로 가리킬 주소 — 클릭 추적용 `staychat.kr/r/<코드>`(tracked-link.ts).
   * 본문에서 예약 링크를 걷어내는 비교는 **언제나 reservationUrl(원래 주소)** 로 한다.
   */
  buttonUrl?: string;
}

export interface ResolvedReply {
  /** 버튼으로 옮긴 링크를 걷어낸 본문. 다 걷어내 비면 '' — 채널이 자기 말로 채운다. */
  text: string;
  actions: ReplyAction[];
}

/**
 * 본문 + meta → 보낼 본문과 행동 목록.
 *
 * 예약 버튼 원칙은 "답변이 그 링크를 실제로 전달했을 때만"이다 — 아무 턴에나 예약을
 * 밀지 않으려고. 다만 ctaForce 턴에는 글에 링크가 없어도 붙인다(물어본 사람에게 갈 곳을
 * 안 주면 안 된다. 실측 2026-09-06 웹 · 2026-09-07 카카오).
 */
export function resolveReply(text: string, meta: ReplyMeta): ResolvedReply {
  const actions: ReplyAction[] = [];
  let out = text;

  const url = meta.reservationUrl;
  if (url) {
    const delivered = findSameTargetUrls(text, url);
    if (delivered.length > 0 || meta.ctaForce === true) {
      out = stripUrls(text, delivered);
      actions.push({
        kind: 'reservation', url: meta.buttonUrl || url,
        ...(meta.ctaLabel ? { label: meta.ctaLabel } : {}),
        ...(meta.bookingSummary ? { summary: meta.bookingSummary } : {}),
      });
    }
  }

  const phone = meta.handoff?.phone?.trim();
  const tel = (phone ?? '').replace(/[^0-9+]/g, '');
  if (phone && tel) actions.push({ kind: 'call', phone, tel });
  const contact = meta.handoff?.contactUrl?.trim();
  if (contact && /^https?:\/\//i.test(contact)) actions.push({ kind: 'contact', url: contact });

  return { text: out, actions };
}

/**
 * 버튼이 없는 글자 채널(인스타 DM 등)용 — 버튼 대신 **마지막 줄에 주소**를 붙인다.
 *   예약하기   서버가 준 예약 주소(모델이 쓴 변형 URL 은 걷어낸다)
 *   문의 남기기 사장님 문의폼 — 사장님 연결 문구가 "아래 문의를 남겨 주시면"이라 주소가 없으면
 *              가리키는 곳이 없다
 * 전화는 붙이지 않는다 — 사장님 연결 문구 본문에 번호가 이미 들어 있다(rag/handoff.ts).
 * 행동이 없으면 본문을 그대로 돌려준다.
 */
export function textWithActionLinks(text: string, meta: ReplyMeta, emptyLead = '아래에서 바로 예약하실 수 있어요 😊'): string {
  const r = resolveReply(text, meta);
  const lines: string[] = [];
  for (const a of r.actions) {
    if (a.kind === 'reservation') lines.push(`${a.label || '예약하기'}: ${a.url}`);
    else if (a.kind === 'contact') lines.push(`문의 남기기: ${a.url}`);
  }
  if (lines.length === 0) return text;
  const head = reservationAction(r.actions) ? (r.text || emptyLead) : text;
  return `${head}\n\n${lines.join('\n')}`;
}

/**
 * 스트림으로 받은 행동 목록을 믿기 전에 거른다 — 모양과 주소 스킴만 통과시킨다.
 * 서버가 만든 값이라도 받는 쪽(위젯·외부 클라이언트)은 입력으로 다룬다:
 * 링크는 http(s) 만, 전화는 숫자·+ 만, 모르는 kind 는 버린다.
 */
export function sanitizeActions(raw: unknown): ReplyAction[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const http = (u: unknown): u is string => typeof u === 'string' && /^https?:\/\//i.test(u);
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : undefined);
  const out: ReplyAction[] = [];
  for (const a of raw as Record<string, unknown>[]) {
    if (!a || typeof a !== 'object') continue;
    if (a.kind === 'reservation' && http(a.url)) {
      const label = str(a.label); const summary = str(a.summary);
      out.push({ kind: 'reservation', url: a.url, ...(label ? { label } : {}), ...(summary ? { summary } : {}) });
    } else if (a.kind === 'call' && typeof a.phone === 'string') {
      const tel = a.phone.replace(/[^0-9+]/g, '');
      if (tel) out.push({ kind: 'call', phone: a.phone, tel });
    } else if (a.kind === 'contact' && http(a.url)) {
      out.push({ kind: 'contact', url: a.url });
    } else if (a.kind === 'ask' && typeof a.text === 'string' && a.text.trim() && a.text.length <= 80) {
      out.push({ kind: 'ask', text: a.text });
    }
  }
  return out;
}

/**
 * 빠른 답장 버튼 글자 — 채널마다 상한이 짧다(네이버 10 · 카카오 14 · 인스타 20).
 * 괄호 속 부연·장식 기호를 먼저 걷고, 그래도 길면 자르고 말줄임표. 누르면 **원문 전체**가
 * 가도록 채널이 따로 싣는다(네이버 code · 인스타 payload · 카카오 messageText).
 */
export function shortLabel(text: string, max: number): string {
  const s = text
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^\p{L}\p{N}\s/·?!.,&+-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim() || text.trim();
  const chars = [...s];
  return chars.length <= max ? s : `${chars.slice(0, max - 1).join('').trimEnd()}…`;
}

/** 이 답변에 예약 버튼이 붙나 — 저장할 때 "링크를 실제로 전달했나"를 적는 자리가 쓴다. */
export function reservationDelivered(text: string, url: string | undefined, force: boolean): boolean {
  return !!url && (force || findSameTargetUrls(text, url).length > 0);
}

export function reservationAction(actions: ReplyAction[]): Extract<ReplyAction, { kind: 'reservation' }> | undefined {
  return actions.find((a): a is Extract<ReplyAction, { kind: 'reservation' }> => a.kind === 'reservation');
}

/**
 * 본문에서 예약 URL 과 **같은 곳을 가리키는** URL 들.
 *
 * 서버는 예약 URL 에 utm 을 붙여 내려보내는데, 모델이 그 쿼리를 흘리거나 순서를 바꿔 쓴다.
 * 글자가 똑같을 때만 같다고 보면 한 글자만 달라도 버튼이 안 붙고 맨 URL 이 남는다(실측
 * 2026-09-06). 그래서 **주소(호스트+경로)가 같으면 같은 곳**으로 본다. 버튼에 거는 주소는
 * 언제나 서버 값이라, 모델이 URL 을 변형해도 안전하다.
 */
export function findSameTargetUrls(text: string, reservationUrl: string): string[] {
  const target = urlTarget(reservationUrl);
  if (!target) return [];
  const out: string[] = [];
  // 마크다운 괄호·따옴표·문장부호는 URL 밖으로 둔다.
  const re = /https?:\/\/[^\s<>"'\])]+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const raw = m[0].replace(/[.,;!?]+$/, '');
    if (urlTarget(raw) === target) out.push(raw);
  }
  // 긴 것부터 지워야 짧은 것이 긴 것의 앞부분만 갉아먹지 않는다.
  return [...new Set(out)].sort((a, b) => b.length - a.length);
}

function urlTarget(u: string): string | null {
  try {
    const x = new URL(u);
    return `${x.host}${x.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

/**
 * 주소를 걷어낸 뒤 **이름표만 남은 줄** — "예약 링크:" · "👉 예약하기" · "실시간 잔여 보기" 같은 것.
 * 주소가 버튼으로 갔으니 이름표는 가리킬 것이 없다(실측 2026-10-03 네이버: "예약 링크: <주소>" 줄에서
 * 주소만 빠져 "예약 링크"가 홀로 남았다). **주소가 있던 줄에만** 적용한다 — 다른 줄의
 * "예약 페이지에서 확인해 주세요" 같은 문장은 건드리지 않는다.
 */
function isLabelOnly(line: string): boolean {
  const core = line
    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, '')
    .replace(/[\s\-–—•·*>▶►➡→:：()[\]!.~,]/g, '');
  // 이름표(명사)만 — "여기서 예약"·"예약은 여기서 하실 수 있어요" 같은 문장은 남긴다(버튼을 가리키는 말이 된다).
  return /^(?:실시간)?(?:예약|잔여|객실|빈방)?(?:링크|바로가기|주소|페이지|보기|하기|확인|확인하기)$/.test(core) || core === '예약';
}

/** 버튼으로 옮길 URL 들을 본문에서 걷어낸다 — `[라벨](url)` 은 통째로, 맨 URL 은 그 자리만, 이름표만 남은 줄은 통째로. */
function stripUrls(text: string, urls: string[]): string {
  const lines: (string | null)[] = text.split('\n').map(line => {
    let l = line;
    for (const u of urls) {
      const esc = u.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      l = l.replace(new RegExp(`\\[([^\\]]*)\\]\\(${esc}\\)`, 'g'), '').split(u).join('');
    }
    return l !== line && isLabelOnly(l) ? null : l;
  });
  // 주소가 **제 줄에 혼자** 있었으면 이름표는 그 윗줄에 있다 — 「**예약 링크:**⏎https://…」. 위 판정은 주소가 있던 줄만 봐서
  // 이름표가 홀로 남았다(실측 2026-09-01 네이버 답, 2026-10-22 reply-lint 60일 소급이 찾음 — e98dadd0 이 막은 한 줄 꼴의 짝).
  // 빈 줄을 건너 바로 위 한 줄만 본다 — 더 위의 문장은 이 주소를 가리킨 것이 아닐 수 있다.
  text.split('\n').forEach((orig, i) => {
    if (lines[i] === null || lines[i]!.trim() !== '' || orig.trim() === '') return;
    let j = i - 1;
    while (j >= 0 && lines[j] !== null && lines[j]!.trim() === '') j--;
    if (j >= 0 && lines[j] !== null && isLabelOnly(lines[j]!)) lines[j] = null;
  });
  const s = lines.filter((l): l is string => l !== null).join('\n');
  return s
    .replace(/[ \t]*[:：]\s*$/gm, '')   // 줄 끝 "여기서:" 댕글링 콜론
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
